import React, { useState, useEffect } from 'react';
import { API_URL } from '../utils/Function';
import { useNavigate, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [services, setServices] = useState([]);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch services from API
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch(`${API_URL}/service.php/get`);
        const data = await response.json();
        
        if (data.success) {
          setServices(data.services);
        }
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };

    fetchServices();
  }, []);

  // Check if device is mobile
  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth < 992);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    
    return () => {
      window.removeEventListener('resize', checkDevice);
    };
  }, []);

  // Prevent body scroll when offcanvas is open
  useEffect(() => {
    if (showOffcanvas) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showOffcanvas]);

  const toggleOffcanvas = () => {
    setShowOffcanvas(!showOffcanvas);
  };

  const closeOffcanvas = () => {
    setShowOffcanvas(false);
    setShowServicesDropdown(false);
  };

  const toggleServicesDropdown = () => {
    setShowServicesDropdown(!showServicesDropdown);
  };

  // Handle service navigation
  const handleServiceNavigation = (serviceSlug) => {
    closeOffcanvas();
    setShowServicesDropdown(false);
    navigate(`/services/${serviceSlug}`);
  };

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeOffcanvas();
        setShowServicesDropdown(false);
      }
    };

    if (showOffcanvas || showServicesDropdown) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showOffcanvas, showServicesDropdown]);

  // Navigation function
  const handleNavigation = (path) => {
    closeOffcanvas();
    
    // If we're already on the main page and navigating to home, scroll to top
    if (path === '/' && window.location.pathname === '/') {
      scrollToSection('home');
    } else {
      navigate(path);
    }
  };

  // Smooth scroll function
  const scrollToSection = (sectionId) => {
    closeOffcanvas();
    
    if (sectionId === 'home') {
      // Scroll to top for home
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      const offsetTop = element.getBoundingClientRect().top + window.pageYOffset - 100; // Adjust for navbar height
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  const navItems = [
    { name: 'Home', id: 'home', type: 'scroll' },
    { name: 'About Us', id: 'about-us', type: 'page', path: '/about-safproview' },
    { name: 'Services', id: 'services', type: 'scroll' },
    { name: 'Why Choose Us', id: 'why-choose-us', type: 'scroll' },
    { name: 'Products', id: 'products', type: 'page', path: '/Productsview' },
    { name: 'Blogs', id: 'blogs', type: 'page', path: '/blogs' },
    { name: 'Contact Us', id: 'contact-us', type: 'page', path: '/Contactus' }
  ];

  const handleNavClick = (item) => {
    if (item.type === 'page') {
      handleNavigation(item.path);
    } else {
      // For scroll items, check if we're on the main page
      if (window.location.pathname === '/') {
        // We're already on main page, just scroll
        scrollToSection(item.id);
      } else {
        // We're on a different page, navigate to main page with section info in state
        navigate('/', { state: { scrollTo: item.id } });
      }
    }
  };

  return (
    <>
      <nav style={{
        backgroundColor: '#ffffff',
        padding: '10px 0',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1030,
        fontFamily: 'Poppins, sans-serif'
      }}>
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            {/* Logo */}
            <div className="logo">
              <a 
                // onClick={()=>navigate('/')}
                style={{ display: 'block' }}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick({ id: 'home', type: 'scroll' });
                  navigate('/');
                }}
              >
                <img 
                  src="/images/Safpro-logo.png" 
                  alt="Logo" 
                  style={{ 
                    width: '217px',
                    height: '66px'
                  }}
                />
              </a>
            </div>

            {/* Desktop Navigation Links - Hidden on mobile */}
            <div className="d-none d-lg-block">
              <ul className="navbar-nav d-flex flex-row mb-0 align-items-center">
                {navItems.map((item) => (
                  item.name === 'Services' ? (
                    // Services Dropdown
                    <li 
                      key={item.id}
                      className="nav-item mx-3 position-relative"
                      onMouseEnter={() => setShowServicesDropdown(true)}
                      onMouseLeave={() => setShowServicesDropdown(false)}
                    >
                      <button
                        className="nav-link fw-medium position-relative border-0 bg-transparent d-flex align-items-center"
                        style={{ 
                          color: '#333', 
                          fontSize: '16px',
                          padding: '8px 0',
                          cursor: 'pointer',
                          fontFamily: 'Poppins, sans-serif'
                        }}
                        onClick={toggleServicesDropdown}
                      >
                        Services
                        <span style={{ marginLeft: '5px', fontSize: '12px' }}>▼</span>
                      </button>
                      
                      {/* Services Dropdown Menu */}
                      {showServicesDropdown && (
                        <div 
                          className="dropdown-menu show"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            backgroundColor: '#ffffff',
                            minWidth: '250px',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '10px 0',
                            zIndex: 1000
                          }}
                        >
                          {services.length > 0 ? (
                            services.map((service) => (
                              <button
                                key={service.id}
                                className="dropdown-item"
                                style={{
                                  padding: '10px 20px',
                                  color: '#333',
                                  textDecoration: 'none',
                                  fontSize: '14px',
                                  fontFamily: 'Poppins, sans-serif',
                                  border: 'none',
                                  backgroundColor: 'transparent',
                                  display: 'block',
                                  width: '100%',
                                  textAlign: 'left',
                                  cursor: 'pointer'
                                }}
                                onClick={() => handleServiceNavigation(service.slug)}
                              >
                                {service.title}
                              </button>
                            ))
                          ) : (
                            <div 
                              className="dropdown-item"
                              style={{
                                padding: '10px 20px',
                                color: '#666',
                                fontSize: '14px',
                                fontStyle: 'italic',
                                fontFamily: 'Poppins, sans-serif'
                              }}
                            >
                              Loading services...
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  ) : (
                    // Regular navigation items
                    <li key={item.id} className="nav-item mx-3">
                      <button
                        className="nav-link fw-medium border-0 bg-transparent"
                        style={{ 
                          color: '#333', 
                          fontSize: '16px',
                          padding: '8px 0',
                          fontFamily: 'Poppins, sans-serif',
                          textDecoration: 'none',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleNavClick(item)}
                      >
                        {item.name}
                      </button>
                    </li>
                  )
                ))}
              </ul>
            </div>

            {/* Mobile Toggle Button - Hidden on desktop */}
            <button
              className="navbar-toggler d-lg-none"
              type="button"
              onClick={toggleOffcanvas}
              aria-label="Toggle navigation"
              style={{
                border: 'none',
                background: 'transparent',
                fontSize: '28px',
                color: '#24246E',
                padding: '8px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontFamily: 'Poppins, sans-serif'
              }}
            >
              ☰
            </button>
          </div>
        </div>
      </nav>

      {/* Spacer to prevent content from being hidden behind fixed navbar */}
      <div style={{ height: '40px' }} />

      {/* Offcanvas for Mobile */}
      <div 
        className={`offcanvas-mobile ${showOffcanvas ? 'open' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: isMobile ? '85%' : '400px',
          maxWidth: '400px',
          backgroundColor: '#ffffff',
          boxShadow: '-2px 0 15px rgba(0,0,0,0.1)',
          transform: showOffcanvas ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 1050,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'Poppins, sans-serif'
        }}
      >
        <div className="offcanvas-header" style={{ 
          borderBottom: '1px solid #eee',
          padding: '20px',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <img 
              src="/images/Safpro-logo.png" 
              alt="Logo" 
              style={{ 
                width: '150px',
                height: '45px'
              }}
            />
            <button
              type="button"
              onClick={closeOffcanvas}
              aria-label="Close menu"
              style={{
                border: 'none',
                background: 'transparent',
                fontSize: '32px',
                color: '#24246E',
                cursor: 'pointer',
                padding: '5px',
                borderRadius: '4px',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'Poppins, sans-serif'
              }}
            >
              ×
            </button>
          </div>
        </div>
        
        <div className="offcanvas-body" style={{ 
          padding: '0',
          flex: 1,
          overflowY: 'auto'
        }}>
          <ul className="navbar-nav">
            {navItems.map((item) => (
              item.name === 'Services' ? (
                // Services Section in Mobile Menu
                <li key={item.id} className="nav-item">
                  <button
                    className="fw-medium d-flex justify-content-between align-items-center py-4 px-4 w-100 border-0 bg-transparent text-start"
                    style={{ 
                      color: '#333', 
                      fontSize: '18px',
                      borderBottom: '1px solid #f8f9fa',
                      fontFamily: 'Poppins, sans-serif',
                      cursor: 'pointer'
                    }}
                    onClick={toggleServicesDropdown}
                  >
                    Services
                    <span style={{ 
                      fontSize: '14px',
                      transition: 'transform 0.3s ease',
                      transform: showServicesDropdown ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}>
                      ▼
                    </span>
                  </button>
                  {showServicesDropdown && (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {services.length > 0 ? (
                        services.map((service) => (
                          <li key={service.id}>
                            <button
                              className="d-block py-3 px-5 w-100 text-start border-0 bg-transparent"
                              style={{
                                color: '#666',
                                fontSize: '16px',
                                textDecoration: 'none',
                                borderBottom: '1px solid #f8f9fa',
                                fontFamily: 'Poppins, sans-serif',
                                cursor: 'pointer'
                              }}
                              onClick={() => handleServiceNavigation(service.slug)}
                            >
                              {service.title}
                            </button>
                          </li>
                        ))
                      ) : (
                        <li className="py-3 px-5" style={{ color: '#999', fontSize: '14px', fontStyle: 'italic' }}>
                          Loading services...
                        </li>
                      )}
                    </ul>
                  )}
                </li>
              ) : (
                // Regular navigation items in mobile
                <li key={item.id} className="nav-item">
                  <button
                    className="nav-link d-block py-4 px-4 fw-medium border-0 bg-transparent w-100 text-start"
                    style={{ 
                      color: '#333', 
                      fontSize: '18px',
                      borderBottom: '1px solid #f8f9fa',
                      textDecoration: 'none',
                      fontFamily: 'Poppins, sans-serif',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleNavClick(item)}
                  >
                    {item.name}
                  </button>
                </li>
              )
            ))}
          </ul>
        </div>
        
        {/* Optional: Add footer to offcanvas */}
        <div style={{
          padding: '20px',
          borderTop: '1px solid #eee',
          textAlign: 'center',
          flexShrink: 0
        }}>
          <p style={{ 
            color: '#666', 
            fontSize: '14px',
            margin: 0,
            fontFamily: 'Poppins, sans-serif'
          }}>
            &copy; {new Date().getFullYear()} Safpro. All rights reserved.
          </p>
        </div>
      </div>

      {/* Backdrop for offcanvas */}
      <div 
        className={`backdrop ${showOffcanvas ? 'visible' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1040,
          opacity: showOffcanvas ? 1 : 0,
          visibility: showOffcanvas ? 'visible' : 'hidden',
          transition: 'all 0.3s ease',
          cursor: 'pointer'
        }}
        onClick={closeOffcanvas}
      />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
        
        /* Apply Poppins font to all elements */
        * {
          font-family: 'Poppins';
        }
        
        /* Dropdown styles */
        .dropdown-menu {
          animation: fadeIn 0.2s ease-in-out;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        /* Responsive adjustments */
        @media (max-width: 480px) {
          .offcanvas-mobile {
            width: 100% !important;
            max-width: 100% !important;
          }
          
          nav {
            height: 80px !important;
          }
          
          .logo img {
            width: 160px !important;
            height: 50px !important;
          }
          
          .spacer {
            height: 80px !important;
          }
        }
        
        @media (max-width: 768px) {
          .nav-link {
            font-size: 16px !important;
            padding: 12px 16px !important;
          }
        }
      `}</style>
    </>
  );
}